import { useQueryParam } from './useQueryParam';
import { deletePaginationParams } from './usePaginationParams';

export const SORT_FIELD_NAME = 'field';
export const SORT_DIRECTION_PARAM = 'direction';

export type SortingDirection = 'desc' | 'asc';

export type SortingValue<SortingField extends string = string> = {
  field: SortingField;
  direction: SortingDirection;
};

export type SortingQuery<SortingField extends string = string> = [
  SortingValue<SortingField> | null,
  (sortingValue: SortingValue<SortingField> | null) => void,
];

export type SortingToggle = [SortingValue | null, (field: string) => void];

export type SortingProps = {
  field: string;
  direction: 'asc' | 'desc';
  onClick?: (field: string) => void;
};

const transformQueryStringToDirection = (queryParameter?: string | null): SortingDirection => {
  if (queryParameter === 'asc') {
    return 'asc';
  }

  return 'desc';
};

export const useSortingParams = <SortingField extends string = string>(params: {
  initialValues?: SortingValue<SortingField>;
  beforeParamName?: string;
  afterParamName?: string;
  sortFieldParamName?: string;
  sortDirectionParamName?: string;
}): SortingQuery<SortingField> => {
  const {
    initialValues,
    beforeParamName,
    afterParamName,
    sortFieldParamName = SORT_FIELD_NAME,
    sortDirectionParamName = SORT_DIRECTION_PARAM,
  } = params;
  return useQueryParam<SortingValue<SortingField> | null>(
    (searchParams) => {
      const field = searchParams.get(sortFieldParamName) as SortingField | null;
      const direction = transformQueryStringToDirection(searchParams.get(sortDirectionParamName));

      if (!field && initialValues) {
        return initialValues;
      }

      if (!field) {
        return null;
      }

      return { field: field, direction };
    },
    (value, searchParams) => {
      if (value) {
        searchParams.set(sortFieldParamName, value.field);
        searchParams.set(sortDirectionParamName, value.direction);
      } else {
        searchParams.delete(sortFieldParamName);
        searchParams.delete(sortDirectionParamName);
      }

      deletePaginationParams(searchParams, beforeParamName, afterParamName);
    },
  );
};

export const useSortingParamsToggle = (
  initialValues?: SortingValue,
  beforeParamName?: string,
  afterParamName?: string,
): SortingToggle => {
  const [sortingValue, setSortingValue] = useSortingParams({
    initialValues,
    beforeParamName,
    afterParamName,
  });

  const toggleSorting = (field: string) => {
    if (!sortingValue) {
      return;
    }

    const isCurrentFieldSorted = field === sortingValue.field;
    const direction = (isCurrentFieldSorted && sortingValue.direction) === 'desc' ? 'asc' : 'desc';

    setSortingValue({ field, direction });
  };

  return [sortingValue, toggleSorting];
};
